import {
  type RepoIssuesDashboardContentResponse,
  RepoIssuesDashboardContentResponseSchema,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {sendEvent} from '@github-ui/hydro-analytics'
import {
  getIssuesIndexContentQueryDeps,
  ISSUES_INDEX_CONTENT_PERSISTER_PREFIX,
  normalizeIssuesIndexContentStorageQueryKey,
} from '@github-ui/issue-index-query-defaults'
import {mainQuery, makeSelectDataFromQueryData} from '@github-ui/react-core/future/main-query'
import {createProtoPersister} from '@github-ui/react-query-persister'

export const ISSUES_INDEX_MAX_AGE = 24 * 60 * 60 * 1000
export const ISSUES_INDEX_ROUTE_ID = 'issuesIndexContentRoute'
export const ISSUES_INDEX_ROUTE_PATH = '/:owner/:repo/issues'

export type IssuesIndexContentQueryData = {
  meta?: {title?: string}
  payload: RepoIssuesDashboardContentResponse
}

function contentError(data: unknown): Error | undefined {
  if (typeof data !== 'object' || data === null || !('payload' in data)) return new Error('Invalid issues response')
  const payload = data.payload
  if (typeof payload !== 'object' || payload === null) return new Error('Invalid issues response')
  if ('error' in payload && payload.error) return new Error('Unable to refresh issues')
  return undefined
}

export function issuesIndexMainQuery({preheat = false}: {preheat?: boolean} = {}) {
  const query = mainQuery<IssuesIndexContentQueryData>({
    schema: RepoIssuesDashboardContentResponseSchema,
    queryDeps: ({pathname, searchParams}) =>
      getIssuesIndexContentQueryDeps({pathname, query: searchParams.get('q'), page: searchParams.get('page')}),
    persister: createProtoPersister({
      schema: RepoIssuesDashboardContentResponseSchema,
      alwaysRefetch: true,
      flag: 'issues_index_query_persistence',
      // Follow-up: evaluate Relay's server-selected TTL; keep retention client-only for now.
      maxAgeMs: ISSUES_INDEX_MAX_AGE,
      getDataError: contentError,
      persistInitialData: true,
      prefix: ISSUES_INDEX_CONTENT_PERSISTER_PREFIX,
      restoreOnPreload: true,
      storageQueryKey: normalizeIssuesIndexContentStorageQueryKey,
      versioning: 'record',
      dataValidator: {
        // Storage instances are shared by validator name, including their analytics callback. A distinct name keeps
        // preheat events tagged without changing the records it reads and writes.
        name: preheat ? 'issues-index-preheat-success' : 'issues-index-success',
        isValid: data => contentError(data) === undefined,
      },
      sendAnalyticsEvent: (event, target, payload) =>
        sendEvent(event, {
          queryName: 'mainQuery',
          routeId: ISSUES_INDEX_ROUTE_ID,
          ...payload,
          ...(preheat && {preheat: true}),
          target,
        }),
    }),
  })

  return {
    ...query,
    select: makeSelectDataFromQueryData<RepoIssuesDashboardContentResponse>(),
  }
}
